/**
 * Team Track Trails Visualization
 * PERFORMANCE OPTIMIZED: Uses canvas for trails, minimal redraws
 */

import * as d3 from 'd3';

export class TrackRenderer {
  constructor(svgElement, mapController) {
    this.svg = d3.select(svgElement);
    this.map = mapController;
    this.canvas = document.getElementById('wake-canvas');
    this.ctx = this.canvas.getContext('2d');

    this.teams = [];
    this.selectedTeam = null;
    this.isInteracting = false;
    this.rafId = null;

    // Simplified path cache for performance
    this.pathCache = new Map();

    this.init();
    this.setupResizeHandler();
  }

  init() {
    // Create defs for filters (minimal, for boat glow only)
    const defs = this.svg.select('defs').empty()
      ? this.svg.append('defs')
      : this.svg.select('defs');

    // Simple glow filter
    if (defs.select('#glow').empty()) {
      const glow = defs.append('filter')
        .attr('id', 'glow')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');

      glow.append('feGaussianBlur')
        .attr('stdDeviation', '2')
        .attr('result', 'coloredBlur');

      const feMerge = glow.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
      feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    }

    // Resize canvas to match container
    this.resizeCanvas();
  }

  setupResizeHandler() {
    const resizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
      this.drawCanvas();
    });
    resizeObserver.observe(this.canvas.parentElement);
  }

  resizeCanvas() {
    const rect = this.canvas.parentElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;

    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Simplify path by reducing points (Douglas-Peucker-like)
   */
  simplifyPath(positions, tolerance = 0.5) {
    if (positions.length <= 2) return positions;

    // For performance, just sample every Nth point based on zoom
    const zoom = this.map.map.getZoom();
    const sampleRate = Math.max(1, Math.floor(8 - zoom));

    if (sampleRate === 1) return positions;

    const simplified = [positions[0]];
    for (let i = sampleRate; i < positions.length - 1; i += sampleRate) {
      simplified.push(positions[i]);
    }
    simplified.push(positions[positions.length - 1]);

    return simplified;
  }

  /**
   * Render all team tracks - called on data change
   */
  render(teams, selectedTeam = null) {
    this.teams = teams;
    this.selectedTeam = selectedTeam;
    this.drawCanvas();
  }

  /**
   * High-performance canvas drawing
   * @param {boolean} immediate - Skip RAF scheduling for sync with map
   */
  drawCanvas(immediate = false) {
    // Always draw immediately for map sync - canvas draw is fast
    this._drawCanvasInternal();
  }

  _drawCanvasInternal() {
    const ctx = this.ctx;
    const width = this.canvas.width / (window.devicePixelRatio || 1);
    const height = this.canvas.height / (window.devicePixelRatio || 1);

    // Clear
    ctx.clearRect(0, 0, width, height);

    if (!this.teams || this.teams.length === 0) return;

    // Sort teams so selected is drawn last (on top)
    const sortedTeams = [...this.teams].sort((a, b) => {
      if (a.serial === this.selectedTeam) return 1;
      if (b.serial === this.selectedTeam) return -1;
      return 0;
    });

    // Draw each team's trail
    sortedTeams.forEach((team, teamIndex) => {
      const positions = team.trailPositions || team.positions;
      if (!positions || positions.length < 2) return;

      const isSelected = team.serial === this.selectedTeam;
      const simplified = this.simplifyPath(positions)
        .filter(pos => !isNaN(pos.lng) && !isNaN(pos.lat));

      if (simplified.length < 2) return;

      // Project points
      const points = simplified.map(pos => this.map.project(pos.lng, pos.lat));

      // Draw trail with gradient effect (brighter at end)
      ctx.beginPath();
      ctx.moveTo(points[0][0], points[0][1]);

      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i][0], points[i][1]);
      }

      // Style based on selection
      ctx.strokeStyle = team.color;
      ctx.lineWidth = isSelected ? 3 : 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = isSelected ? 0.9 : 0.6;

      // Add glow effect for top 3 or selected
      if (isSelected || teamIndex >= sortedTeams.length - 3) {
        ctx.shadowColor = team.color;
        ctx.shadowBlur = isSelected ? 12 : 6;
      } else {
        ctx.shadowBlur = 0;
      }

      ctx.stroke();

      // Reset shadow
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
    });
  }

  /**
   * Update on map move - just redraw canvas (fast!)
   */
  updateOnMapMove() {
    this.drawCanvas();
  }

  /**
   * Update track positions (for timeline scrubbing)
   */
  updatePositions(teamsAtTime) {
    this.teams = teamsAtTime;
    this.drawCanvas();
  }

  /**
   * Set interaction state (for reduced quality during pan/zoom)
   */
  setInteracting(isInteracting) {
    this.isInteracting = isInteracting;
  }

  /**
   * Clear all tracks
   */
  clear() {
    this.teams = [];
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
